import { Dialog } from '@reach/dialog'
import clsx from 'clsx'
import type { FC } from 'react'
import React, { useEffect, useState } from 'react'
import type { Swiper as SwiperRef } from 'swiper'
import { Navigation, Pagination, Thumbs, A11y } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { getSizedImageUrl } from '../../../core/shopify/get-sized-image-url'
import { getSizedImageUrlPreloaded } from '../../../core/shopify/get-sized-image-url-preload'
import { useProduct } from '../client/product/use-product'
import { Close as CloseIcon } from '../ui/icons/close'

interface ImagesProps {
  productId: string
  className?: string
  defaultImage?: string
  onLoaded: () => void
}

export const Images: FC<ImagesProps> = ({
  productId,
  className,
  defaultImage,
  onLoaded,
}) => {
  const [images, setImages] = useState<string[]>([])
  const [thumbsSwiper, setThumbsSwiper] = useState<null | SwiperRef>(null)

  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false)
  const [activeIndex, setActiveIndex] = useState(0)

  const product = useProduct(productId)

  useEffect(() => {
    if (product?.images?.length) {
      setImages(product.images)
    } else if (defaultImage) {
      setImages([defaultImage])
    }
  }, [product, defaultImage])

  const [hasSignaled, setHasSignaled] = useState(false)

  return (
    <div className={clsx('PSImages', className)}>
      <Swiper
        spaceBetween={10}
        slidesPerView={1}
        navigation={{ nextEl: '.swiper-button-next', prevEl: '.swiper-button-prev' }}
        pagination={{ clickable: true }}
        thumbs={{ swiper: thumbsSwiper }}
        modules={[Thumbs, Pagination, Navigation, A11y]}
        loop={false}
        centeredSlides={false}
        slideToClickedSlide={true}
        preventInteractionOnTransition={true}
        className="PSImages-swiper"
        onTransitionEnd={(swiper) => {
          setActiveIndex(swiper.activeIndex)
        }}
      >
        {images.map((e, i) => {
          const preUrl = getSizedImageUrlPreloaded(e, 414, 627)
          const fallbackUrl = getSizedImageUrl(e, '414x627')

          return (
            <SwiperSlide key={i}>
              <img
                src={preUrl}
                srcSet={`
                  ${preUrl} 414w,
                  ${getSizedImageUrlPreloaded(e, 640, 627)} 640w,
                  ${getSizedImageUrlPreloaded(e, 847, 627)} 847w
                `}
                sizes="100vw"
                className="PSImages-img"
                alt={`Product image – ${i + 1}`}
                width={627}
                height={430}
                loading={i === 0 ? 'eager' : 'lazy'}
                fetchPriority={i === 0 ? 'high' : undefined}
                onError={(ev) => {
                  ev.currentTarget.onerror = null
                  ev.currentTarget.removeAttribute('srcset')
                  if (fallbackUrl) {
                    ev.currentTarget.src = fallbackUrl
                  }
                }}
                onLoad={() => {
                 if (i === 0 && !hasSignaled) {
                   setHasSignaled(true)
                   onLoaded()
                 }
                }}
                onClick={() => {
                  setActiveIndex(i)
                  setModalIsOpen(true)
                }}
              />
            </SwiperSlide>
          )
        })}
        <div className="swiper-button-next"></div>
        <div className="swiper-button-prev"></div>
      </Swiper>

      <Swiper
        spaceBetween={10}
        onSwiper={(swiper) => {
          setThumbsSwiper(swiper)
        }}
        slidesPerView={4}
        freeMode={true}
        scrollbar={{ draggable: true }}
        watchSlidesProgress={true}
        preloadImages={false}
        className="PSImages-thumbnails"
      >
        {images.map((e, i) => (
          <SwiperSlide key={i}>
            <img
              src={getSizedImageUrl(e, '120x')}
              width={120}
              height={120}
              loading="lazy"
              alt={`Thumbnail ${i + 1}`}
              className="PSImages-thumb"
            />
          </SwiperSlide>
        ))}
      </Swiper>

      <Dialog
        className={clsx('VideoModal')}
        isOpen={modalIsOpen}
        onDismiss={() => {
          setModalIsOpen(false)
        }}
      >
        <div className={clsx('VideoModal-content')}>
          <Swiper
            spaceBetween={0}
            preloadImages={false}
            slidesPerView={1}
            navigation={{ nextEl: '.swiper-button-next', prevEl: '.swiper-button-prev' }}
            lazy={false}
            initialSlide={activeIndex}
            modules={[Navigation, A11y]}
            loop={false}
            centeredSlides={false}
            slideToClickedSlide={true}
            preventInteractionOnTransition={true}
          >
            {images.map((e, i) => (
              <SwiperSlide key={i}>
                <img
                  src={e}
                  className="PSImages-img"
                  alt={`Product image – large ${i + 1}`}
                />
              </SwiperSlide>
            ))}
            <div className="swiper-button-next"></div>
            <div className="swiper-button-prev"></div>
          </Swiper>

          <div className={clsx('VideoModal-close')}>
            <div className={clsx('VideoModal-closeIconPlaceholder')}>
              <CloseIcon />
            </div>
            <button
              type="button"
              className={clsx('VideoModal-closeButton')}
              onClick={() => setModalIsOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  )
}